// Cliente HTTP con interceptor JWT. Si recibe 401, limpia la sesión y redirige a /login.
import { useAuthStore } from "../stores/auth";

const TOKEN_KEY = "analista_token";
const LEGACY_TOKEN_KEY = "surveypro_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY) || localStorage.getItem(LEGACY_TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(LEGACY_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  }
}

async function request<T>(
  method: string,
  url: string,
  body?: unknown,
  opts: { raw?: boolean; contentType?: string } = {}
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (body !== undefined && !(body instanceof FormData)) {
    headers["Content-Type"] = opts.contentType ?? "application/json";
  }
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const init: RequestInit = {
    method,
    headers,
    body:
      body === undefined
        ? undefined
        : body instanceof FormData
        ? body
        : typeof body === "string"
        ? body
        : JSON.stringify(body),
  };

  const res = await fetch(url, init);

  if (res.status === 401) {
    // Sesión expirada / inválida.
    setToken(null);
    useAuthStore.getState().clear();
    if (!window.location.pathname.startsWith("/login")) {
      window.location.href = "/login";
    }
    throw new Error("No autorizado");
  }

  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
      else if (j?.error) detail = j.error;
    } catch {
      /* sin cuerpo JSON */
    }
    throw new Error(detail);
  }

  if (opts.raw) return res as unknown as T;
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) return (await res.json()) as T;
  return (await res.text()) as unknown as T;
}

export const api = {
  get: <T>(url: string) => request<T>("GET", url),
  post: <T>(url: string, body?: unknown) => request<T>("POST", url, body),
  put: <T>(url: string, body?: unknown) => request<T>("PUT", url, body),
  patch: <T>(url: string, body?: unknown) => request<T>("PATCH", url, body),
  del: <T>(url: string) => request<T>("DELETE", url),
  // Para descargas (blob).
  getBlob: (url: string) =>
    request<Response>("GET", url, undefined, { raw: true }).then((r) => r.blob()),
  postBlob: (url: string, body: unknown) =>
    request<Response>("POST", url, body, { raw: true }).then((r) => r.blob()),
};
